import { type ButtonHTMLAttributes, type ReactNode, forwardRef } from 'react';

/**
 * The prototype's to-do list (`.tlist`, Story #28001 — `#ws-hub` Due Soon and `#ws-todo`): a bordered list whose rows
 * carry a tick (or the read-only `.box`), the item (`.tn`: title, then `.sub` lines) and, on the right, its due chip
 * and actions. Styled by primitives.css.
 */

export interface TaskListProps {
  children: ReactNode;
  /** Accessible name of the list. */
  label: string;
  /** No bottom margin (the prototype's `style="margin:0"` inside a card). */
  flush?: boolean;
}

export function TaskList({ children, label, flush }: TaskListProps) {
  return (
    <ul className={flush ? 'ui-tlist ui-flush' : 'ui-tlist'} aria-label={label}>
      {children}
    </ul>
  );
}

export interface TaskRowProps {
  children: ReactNode;
  id?: string;
  /** Draws the row highlighted — the item a link opened the list at. */
  highlighted?: boolean;
  /** The row holds an in-place editor rather than an item (`li > .fbar`). */
  editing?: boolean;
  testId?: string;
}

export function TaskRow({ children, id, highlighted, editing, testId }: TaskRowProps) {
  const className = [highlighted ? 'ui-hl' : '', editing ? 'ui-editing' : ''].filter(Boolean).join(' ') || undefined;
  return (
    <li id={id} className={className} data-testid={testId} aria-current={highlighted ? 'true' : undefined}>
      {children}
    </li>
  );
}

/** `.tn` — the item: its title, then any `.sub` lines (kind badge, client, job; a description). */
export function TaskTitle({ children }: { children: ReactNode }) {
  return <span className="ui-tn">{children}</span>;
}

/** `.box` — the read-only tick of an item that is ticked elsewhere; `done` draws the green ✓ (`.box.on`). */
export function TaskBox({ done }: { done?: boolean }) {
  return (
    <span className={done ? 'ui-box ui-on' : 'ui-box'} aria-hidden="true">
      {done ? '✓' : ''}
    </span>
  );
}

/** `.insight` — a gold-edged panel: a bold heading line, then supporting lines (the AI brief). */
export function Insight({ title, children, testId }: { title: ReactNode; children: ReactNode; testId?: string }) {
  return (
    <div className="ui-insight" data-testid={testId}>
      <b>{title}</b>
      {children}
    </div>
  );
}

export interface DragGripProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Accessible name, e.g. "Reorder Bank reconciliation". */
  label: string;
}

/**
 * `.grip` — the ⋮⋮ drag handle at the start of a sortable row (Story #28002, `#task-templates`). A real button, so a
 * keyboard sortable can take focus on it; spread the drag library's attributes and listeners onto it.
 */
export const DragGrip = forwardRef<HTMLButtonElement, DragGripProps>(function DragGrip({ label, className = '', ...props }, ref) {
  return (
    <button ref={ref} type="button" aria-label={label} className={['ui-grip', className].filter(Boolean).join(' ')} {...props}>
      <span aria-hidden="true">⋮⋮</span>
    </button>
  );
});
